import { useTranslation } from 'react-i18next';
import {
    Form,
    Grid,
    IOption,
    PargodaRoundButton,
    useAppDispatch,
    useAppSelector,
    useTheme,
    EnvTypes,
    RootState,
} from '../../base';
import BasicStepper from '../BasicStepper';
import {
    StyledSaveJobBox,
    StyledSaveJobContainer,
    StyledStepperWrap,
} from './SaveJob.styles';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
    SAVE_JOB_FORM_VALUES,
    saveJobSchema,
    emptySaveJobForm,
    ISaveJobForm,
    ISaveJobRequirement,
    ISaveJobBenefit,
    IJobAttribute,
    JobStatus,
    IJobStepObject,
    FeatureFlags,
    QuestionType,
    normalizeRequirementRange,
} from '../../common';
import {
    jobsActions,
    jobsSelectors,
    ISaveJobAction,
    organizationSelectors,
    formatApplyQuestionsForForm,
    userSelectors,
} from '../../actions';
import {
    SaveJobPreview,
    JobDescriptionAndAttributes,
    JobRequirementsAndBenefits,
    JobInterviewSteps,
    JobQuestions,
} from './SaveJobSteps';
import { getQuestionTypeLabelKey } from './SaveJobSteps/JobQuestions/constants';
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { RoutePaths } from '../../routes/RoutePaths';
import { DevTool } from '@hookform/devtools';
import ProceedWithRecommendedModal from './SaveJobSteps/JobRequirementsAndBenefits/ProceedWithRecommendedModal';

interface Props {
    id?: string;
    isClone?: boolean;
}

const SaveJob = (props: Props) => {
    const { id, isClone } = props;
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    const activeJob = useAppSelector(jobsSelectors.getActiveJob);
    const jobAttributeList = useAppSelector(jobsSelectors.getJobAttributeList);
    const jobBenefitsOptions = useAppSelector(
        jobsSelectors.getJobBenefitsOptions
    );
    const IsUpdateJobLoading = useAppSelector(
        jobsSelectors.getIsUpdateJobLoading
    );
    const isProceedWithRecommendedModalOpen = useAppSelector(
        jobsSelectors.getIsProceedWithRecommendedModalOpen
    );
    const stepperInfo = useAppSelector(jobsSelectors.getJobStepperInfo);
    const jobOverviewAndResponsibilities = useAppSelector(
        jobsSelectors.getJobOverviewAndResponsibilities
    );
    const currentUser = useAppSelector(userSelectors.getMyUser);
    const companySettings = useAppSelector(
        organizationSelectors.getCompanySettings
    );

    // TODO: Remove this feature flag when JobQuestions is ready for production
    const isCandidateQuestionsFeatureFlagOn = useAppSelector(
        (state: RootState) =>
            organizationSelectors.isFlagOn(
                state,
                FeatureFlags.CANDIDATE_QUESTIONS
            )
    );

    useEffect(() => {
        if (jobOverviewAndResponsibilities) {
            console.log(jobOverviewAndResponsibilities);
        }
    }, [jobOverviewAndResponsibilities]);

    const getFormDefaultValue = (): ISaveJobForm => {
        if ((id || isClone) && activeJob) {
            return {
                ...emptySaveJobForm,
                [SAVE_JOB_FORM_VALUES.TITLE]: activeJob.title,
                [SAVE_JOB_FORM_VALUES.JOB_OVERVIEW]: activeJob.description,
                [SAVE_JOB_FORM_VALUES.EXTERNAL_JOB_ID]: activeJob.externalJobId,
                [SAVE_JOB_FORM_VALUES.LOCATION]: activeJob.location,
                [SAVE_JOB_FORM_VALUES.COMPANY_OVERVIEW]:
                    activeJob.companyOverview,
                [SAVE_JOB_FORM_VALUES.RESPONSIBILITIES]:
                    activeJob.responsibilities,
                [SAVE_JOB_FORM_VALUES.RECRUITER]: activeJob.recruiter
                    ? {
                          label: `${activeJob.recruiter.firstName} ${activeJob.recruiter.lastName}`,
                          value: String(activeJob.recruiter.id),
                          key: String(activeJob.recruiter.id),
                          iconUrl: activeJob.recruiter.profilePicture,
                      }
                    : null,
                [SAVE_JOB_FORM_VALUES.RECRUITING_MANAGER]:
                    activeJob.recruitingManager
                        ? {
                              label: `${activeJob.recruitingManager.firstName} ${activeJob.recruitingManager.lastName}`,
                              value: String(activeJob.recruitingManager.id),
                              key: String(activeJob.recruitingManager.id),
                              iconUrl:
                                  activeJob.recruitingManager.profilePicture,
                          }
                        : null,

                [SAVE_JOB_FORM_VALUES.ATTRIBUTE]: jobAttributeList.map(
                    (_attribute: IJobAttribute) => {
                        const existingAttribute = activeJob.jobAttributes?.find(
                            (attribute) =>
                                attribute.jobAttributeOption.id ===
                                _attribute.id
                        );
                        return existingAttribute
                            ? {
                                  value: {
                                      label:
                                          existingAttribute.jobAttributeValue
                                              .name || ' ',
                                      value: String(
                                          existingAttribute.jobAttributeValue.id
                                      ),
                                  },
                              }
                            : null;
                    }
                ),
                [SAVE_JOB_FORM_VALUES.JOB_BENEFITS]: jobBenefitsOptions.map(
                    (benefit, index) => ({
                        formId: index,
                        benefitId: String(benefit.id),
                        name: benefit.name,
                        isSelected: !!activeJob.jobBenefits?.some(
                            (jobBenefit) =>
                                jobBenefit.jobBenefitsOption.id === benefit.id
                        ),
                    })
                ),
                [SAVE_JOB_FORM_VALUES.JOB_REQUIREMENTS]:
                    activeJob.jobRequirementGroups?.reduce(
                        (acc: ISaveJobRequirement[], group) => {
                            return [
                                ...acc,
                                ...group.requirements.map((requirement) => {
                                    return {
                                        requirementId:
                                            String(requirement.optionId) ||
                                            null,
                                        isAIRecommendation:
                                            requirement.isAIRecommendation,
                                        name: requirement.optionName,
                                        requirementGroup: group.groupName,
                                        weight: {
                                            from: 0,
                                            to: requirement.weight,
                                        },
                                        rangeFrom: requirement.rangeFrom,
                                        rangeTo: requirement.rangeTo,
                                        importanceLevel:
                                            requirement.importanceLevel,
                                        isVisible: requirement.isVisible,
                                        rangeInd: true,
                                    };
                                }),
                            ];
                        },
                        []
                    ) || [],
                [SAVE_JOB_FORM_VALUES.STEPS]:
                    activeJob.steps?.map((step) => ({
                        id: step.id,
                        stepName: step.name,
                        stepOwner: step.stepOwner,
                        interviewers: step.interviewers || null,
                        isFirstStep: step.isFirstStep,
                        isLastStep: step.isLastStep,
                    })) || [],
                [SAVE_JOB_FORM_VALUES.STEPS_SELECTED_JOB]: null,
                [SAVE_JOB_FORM_VALUES.QUESTIONS]: formatApplyQuestionsForForm(
                    activeJob.applyQuestions,
                    (type: QuestionType) =>
                        t(`saveJob.${getQuestionTypeLabelKey(type)}`)
                ),
                [SAVE_JOB_FORM_VALUES.QUESTIONS_SELECTED_JOB]: null,
            };
        }
        return {
            ...emptySaveJobForm,
            [SAVE_JOB_FORM_VALUES.TITLE]: '',
            [SAVE_JOB_FORM_VALUES.COMPANY_OVERVIEW]:
                companySettings?.companyInfo?.description || '',
            [SAVE_JOB_FORM_VALUES.ATTRIBUTE]: jobAttributeList.map(
                (_attribute) => null
            ),
            [SAVE_JOB_FORM_VALUES.RECRUITER]: currentUser
                ? {
                      label: `${currentUser.firstName} ${currentUser.lastName}`,
                      value: String(currentUser.id),
                      key: String(currentUser.id),
                      iconUrl: currentUser.profilePicture,
                  }
                : null,
            [SAVE_JOB_FORM_VALUES.JOB_BENEFITS]: jobBenefitsOptions.map(
                (benefit, index) => ({
                    formId: index,
                    benefitId: String(benefit.id),
                    name: benefit.name,
                    isSelected: false,
                })
            ),
        };
    };

    const methods = useForm<ISaveJobForm>({
        resolver: yupResolver(saveJobSchema(t)),
        mode: 'onSubmit',
        defaultValues: getFormDefaultValue(),
    });

    const { control, getValues, reset, trigger } = methods;

    const requirementsArray = useFieldArray<
        ISaveJobForm,
        SAVE_JOB_FORM_VALUES.JOB_REQUIREMENTS,
        'requirementId'
    >({
        control,
        name: SAVE_JOB_FORM_VALUES.JOB_REQUIREMENTS,
    });

    const benefitsArray = useFieldArray<
        ISaveJobForm,
        SAVE_JOB_FORM_VALUES.JOB_BENEFITS,
        'benefitId'
    >({
        control,
        name: SAVE_JOB_FORM_VALUES.JOB_BENEFITS,
    });

    const stepsArray = useFieldArray<
        ISaveJobForm,
        SAVE_JOB_FORM_VALUES.STEPS,
        'id'
    >({
        control,
        name: SAVE_JOB_FORM_VALUES.STEPS,
    });

    const questionsArray = useFieldArray<
        ISaveJobForm,
        SAVE_JOB_FORM_VALUES.QUESTIONS,
        'id'
    >({
        control,
        name: SAVE_JOB_FORM_VALUES.QUESTIONS,
    });

    // Non-draft jobs are already published — skip the preview/publish step
    const showPublishStep = !(
        id &&
        !isClone &&
        activeJob &&
        activeJob.status !== JobStatus.Draft
    );

    // Calculate step indices based on feature flag + whether publish step is shown
    const stepIndices = useMemo(() => {
        if (isCandidateQuestionsFeatureFlagOn) {
            return {
                jobInterviewSteps: 2,
                jobQuestions: 3 as number | undefined,
                saveJobPreview: showPublishStep ? 4 : undefined,
                lastContentStep: 3,
            };
        }
        return {
            jobInterviewSteps: 2,
            jobQuestions: undefined as number | undefined,
            saveJobPreview: showPublishStep ? 3 : undefined,
            lastContentStep: 2,
        };
    }, [isCandidateQuestionsFeatureFlagOn, showPublishStep]);

    const lastStepIndex = showPublishStep
        ? stepIndices.saveJobPreview!
        : stepIndices.lastContentStep;

    const getFieldsForStep = (
        stepIndex: number
    ): Array<keyof ISaveJobForm> => {
        if (stepIndex === 0) {
            return [
                SAVE_JOB_FORM_VALUES.TITLE,
                SAVE_JOB_FORM_VALUES.LOCATION,
                SAVE_JOB_FORM_VALUES.EXTERNAL_JOB_ID,
                SAVE_JOB_FORM_VALUES.ATTRIBUTE,
                SAVE_JOB_FORM_VALUES.RECRUITING_MANAGER,
                SAVE_JOB_FORM_VALUES.RECRUITER,
                SAVE_JOB_FORM_VALUES.COMPANY_OVERVIEW,
                SAVE_JOB_FORM_VALUES.JOB_OVERVIEW,
                SAVE_JOB_FORM_VALUES.RESPONSIBILITIES,
            ];
        }
        if (stepIndex === 1) {
            return [
                SAVE_JOB_FORM_VALUES.JOB_REQUIREMENTS,
                SAVE_JOB_FORM_VALUES.JOB_BENEFITS,
            ];
        }
        if (stepIndex === stepIndices.jobInterviewSteps) {
            return [SAVE_JOB_FORM_VALUES.STEPS];
        }
        if (
            stepIndices.jobQuestions !== undefined &&
            stepIndex === stepIndices.jobQuestions
        ) {
            return [SAVE_JOB_FORM_VALUES.QUESTIONS];
        }
        return [];
    };

    const buildJobPayloadFromFormData = (
        formData: Record<string, any>
    ): ISaveJobAction['job'] => {
        const job: ISaveJobAction['job'] = {};

        if (SAVE_JOB_FORM_VALUES.TITLE in formData) {
            job.title = formData[SAVE_JOB_FORM_VALUES.TITLE];
        }
        if (SAVE_JOB_FORM_VALUES.JOB_OVERVIEW in formData) {
            job.description =
                formData[SAVE_JOB_FORM_VALUES.JOB_OVERVIEW] || undefined;
        }
        if (SAVE_JOB_FORM_VALUES.EXTERNAL_JOB_ID in formData) {
            job.external_job_id =
                formData[SAVE_JOB_FORM_VALUES.EXTERNAL_JOB_ID];
        }
        if (SAVE_JOB_FORM_VALUES.LOCATION in formData) {
            job.location = formData[SAVE_JOB_FORM_VALUES.LOCATION];
        }
        if (SAVE_JOB_FORM_VALUES.COMPANY_OVERVIEW in formData) {
            job.company_overview =
                formData[SAVE_JOB_FORM_VALUES.COMPANY_OVERVIEW] || undefined;
        }
        if (SAVE_JOB_FORM_VALUES.RESPONSIBILITIES in formData) {
            job.responsibilities =
                formData[SAVE_JOB_FORM_VALUES.RESPONSIBILITIES] || undefined;
        }
        if (SAVE_JOB_FORM_VALUES.RECRUITER in formData) {
            job.recruiter =
                formData[SAVE_JOB_FORM_VALUES.RECRUITER]?.value ||
                (currentUser ? String(currentUser.id) : undefined);
        }
        if (SAVE_JOB_FORM_VALUES.RECRUITING_MANAGER in formData) {
            job.recruiting_manager =
                formData[SAVE_JOB_FORM_VALUES.RECRUITING_MANAGER]?.value ||
                undefined;
        }
        if (SAVE_JOB_FORM_VALUES.ATTRIBUTE in formData) {
            job.job_attributes = formData[SAVE_JOB_FORM_VALUES.ATTRIBUTE]
                ?.filter((attribute: { value: IOption }) => {
                    return attribute?.value?.value;
                })
                .map(
                    (attribute: { value: IOption }) => attribute?.value?.value
                );
        }
        if (SAVE_JOB_FORM_VALUES.JOB_REQUIREMENTS in formData) {
            job.job_requirements = formData[
                SAVE_JOB_FORM_VALUES.JOB_REQUIREMENTS
            ]?.map((requirement: ISaveJobRequirement) => {
                return {
                    job_requirement_option:
                        requirement.requirementId ?? undefined,
                    recommendation_id:
                        requirement.recommendationId ?? undefined,
                    weight: requirement.weight.to,
                    range_from:
                        normalizeRequirementRange(requirement.rangeFrom) ||
                        null,
                    range_to:
                        normalizeRequirementRange(requirement.rangeTo) || null,
                    importance_level: requirement.importanceLevel,
                    is_visible: requirement.isVisible,
                };
            });
        }
        if (SAVE_JOB_FORM_VALUES.JOB_BENEFITS in formData) {
            job.job_benefits = formData[SAVE_JOB_FORM_VALUES.JOB_BENEFITS]
                ?.filter((benefit: ISaveJobBenefit) => benefit.isSelected)
                .map((benefit: ISaveJobBenefit) => {
                    return {
                        job_benefit_option: benefit.benefitId,
                    };
                });
        }
        if (SAVE_JOB_FORM_VALUES.STEPS in formData) {
            job.steps = formData[SAVE_JOB_FORM_VALUES.STEPS]
                ?.filter(
                    (step: IJobStepObject) =>
                        !step.isFirstStep && !step.isLastStep
                )
                .map((step: IJobStepObject) => {
                    const existingStepIds = new Set(
                        activeJob?.steps?.map((s) => String(s.id))
                    );
                    const isExistingStep = existingStepIds.has(
                        String(step.id)
                    );
                    return {
                        ...(isExistingStep ? { id: Number(step.id) } : {}),
                        name: step.stepName,
                        step_owner: step.stepOwner?.value
                            ? Number(step.stepOwner.value)
                            : undefined,
                    };
                });
        }
        if (SAVE_JOB_FORM_VALUES.QUESTIONS in formData) {
            // Pass raw questions data to saga - formatting happens there
            job.apply_questions = formData[SAVE_JOB_FORM_VALUES.QUESTIONS];
        }

        return job;
    };

    const saveJob = (isMainButtonClicked?: boolean) => {
        const stepFields = getFieldsForStep(stepperInfo.activeStepIndex);
        const dirtyFields = control._formState.dirtyFields as Partial<
            Record<keyof ISaveJobForm, unknown>
        >;

        // Only include fields that belong to the active step.
        // The questions step always sends the full list: nested edits are not
        // always marked dirty, and omitted templates used to stay live for
        // applicants.
        const formData = stepFields.reduce(
            (acc: Record<string, any>, field) => {
                const isQuestionsField =
                    field === SAVE_JOB_FORM_VALUES.QUESTIONS;
                if (isClone || isQuestionsField || dirtyFields[field]) {
                    acc[field] = getValues(field);
                }
                return acc;
            },
            {}
        );

        // Creating a new job from overview always needs title (+ default recruiter)
        if (!id && stepperInfo.activeStepIndex === 0) {
            formData[SAVE_JOB_FORM_VALUES.TITLE] = getValues(
                SAVE_JOB_FORM_VALUES.TITLE
            );
            const recruiter = getValues(SAVE_JOB_FORM_VALUES.RECRUITER);
            if (recruiter) {
                formData[SAVE_JOB_FORM_VALUES.RECRUITER] = recruiter;
            }
        }

        if (Object.keys(formData).length === 0 && !isClone) {
            if (isMainButtonClicked) {
                if (
                    id &&
                    stepperInfo.activeStepIndex === lastStepIndex
                ) {
                    navigate(`${RoutePaths.jobs}/${id}`);
                } else {
                    dispatch(
                        jobsActions.setStepperInfo({
                            stepperInfo: {
                                ...stepperInfo,
                                activeStepIndex:
                                    stepperInfo.activeStepIndex + 1,
                            },
                        })
                    );
                }
            }
            return;
        }

        const saveJobActionData: ISaveJobAction = {
            job: buildJobPayloadFromFormData(formData),
        };

        if (id) {
            dispatch(
                jobsActions.updateActiveJob({
                    jobId: id,
                    ...saveJobActionData,
                    isSaveAndContinue: isMainButtonClicked,
                })
            );
        } else {
            dispatch(
                jobsActions.createJob({
                    ...saveJobActionData,
                    isSaveAndContinue: isMainButtonClicked,
                })
            );
        }
    };

    const saveJobSubmit = async () => {
        const stepFields = getFieldsForStep(stepperInfo.activeStepIndex);
        const isValid =
            stepFields.length === 0 ? true : await trigger(stepFields);
        if (isValid) {
            saveJob();
        }
    };

    const onMainButtonClicked = async () => {
        const previewStepIndex = stepIndices.saveJobPreview;
        if (
            previewStepIndex !== undefined &&
            stepperInfo.activeStepIndex === previewStepIndex
        ) {
            dispatch(
                jobsActions.updateJobStatus({
                    jobId: String(id),
                    status: JobStatus.Published,
                })
            );
            return;
        }

        const stepFields = getFieldsForStep(stepperInfo.activeStepIndex);
        const isValid =
            stepFields.length === 0 ? true : await trigger(stepFields);
        if (isValid) {
            saveJob(true);
        }
    };

    const getMainButtonLabel = () => {
        if (
            stepIndices.saveJobPreview !== undefined &&
            stepperInfo.activeStepIndex === stepIndices.saveJobPreview
        ) {
            return t('saveJob.publish');
        }
        if (
            !showPublishStep &&
            stepperInfo.activeStepIndex === lastStepIndex
        ) {
            return t('saveJob.saveAndFinish');
        }
        return t('saveJob.saveAndContinue');
    };

    const onPrevButtonClicked = () => {
        dispatch(
            jobsActions.setStepperInfo({
                stepperInfo: {
                    ...stepperInfo,
                    activeStepIndex: stepperInfo.activeStepIndex - 1,
                },
            })
        );
    };

    useEffect(() => {
        if (!id || activeJob) {
            reset(getFormDefaultValue());
        }
    }, [id, activeJob, companySettings]);

    // Update stepper length from feature flag + publish-step visibility
    useEffect(() => {
        // Wait for the job to load when editing so we know draft vs published
        if (id && !activeJob) {
            return;
        }

        const totalSteps = lastStepIndex + 1;
        const currentSteps = stepperInfo.steps.length;

        if (currentSteps !== totalSteps) {
            dispatch(
                jobsActions.setStepperInfo({
                    stepperInfo: {
                        steps: Array.from({ length: totalSteps }, (_, i) => ({
                            labelText: String(i + 1),
                        })),
                        activeStepIndex: Math.min(
                            stepperInfo.activeStepIndex,
                            lastStepIndex
                        ),
                    },
                })
            );
        }
    }, [
        isCandidateQuestionsFeatureFlagOn,
        showPublishStep,
        lastStepIndex,
        id,
        activeJob,
    ]);

    return (
        <>
            <Form methods={methods} onSubmit={saveJobSubmit}>
                <StyledSaveJobContainer container gap={6}>
                    <Grid item xs={12}>
                        <StyledSaveJobBox>
                            <StyledStepperWrap>
                                <BasicStepper stepperInfo={stepperInfo} />
                            </StyledStepperWrap>
                            {stepperInfo?.activeStepIndex === 0 && (
                                <JobDescriptionAndAttributes
                                    methods={methods}
                                />
                            )}
                            {stepperInfo?.activeStepIndex === 1 && (
                                <JobRequirementsAndBenefits
                                    requirementsArray={requirementsArray}
                                    benefitsArray={benefitsArray}
                                />
                            )}
                            {stepperInfo?.activeStepIndex ===
                                stepIndices.jobInterviewSteps && (
                                <JobInterviewSteps stepsArray={stepsArray} />
                            )}
                            {isCandidateQuestionsFeatureFlagOn &&
                                stepperInfo?.activeStepIndex ===
                                    stepIndices.jobQuestions && (
                                    <JobQuestions
                                        questionsArray={questionsArray}
                                    />
                                )}
                            {stepIndices.saveJobPreview !== undefined &&
                                stepperInfo?.activeStepIndex ===
                                    stepIndices.saveJobPreview && (
                                    <SaveJobPreview />
                                )}
                            {stepperInfo?.activeStepIndex <=
                                stepIndices.lastContentStep && (
                                <Grid
                                    container
                                    item
                                    xs={12}
                                    justifyContent="flex-end"
                                    pt={2}
                                >
                                    <Grid item>
                                        <PargodaRoundButton
                                            variant="outlined"
                                            buttonColor={
                                                theme.palette.secondary.main
                                            }
                                            loading={IsUpdateJobLoading}
                                            type="button"
                                            onClick={saveJobSubmit}
                                            sx={{ width: '100px', height: 48 }}
                                        >
                                            {t('saveJob.save')}
                                        </PargodaRoundButton>
                                    </Grid>
                                </Grid>
                            )}
                        </StyledSaveJobBox>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container gap={1.5} justifyContent="flex-end">
                            <PargodaRoundButton
                                variant="outlined"
                                buttonColor={theme.palette.primary.main}
                                disabled={stepperInfo?.activeStepIndex === 0}
                                loading={IsUpdateJobLoading}
                                onClick={onPrevButtonClicked}
                                sx={{ width: '214px', height: 48 }}
                            >
                                {t('saveJob.previous')}
                            </PargodaRoundButton>
                            <PargodaRoundButton
                                variant="contained"
                                onClick={onMainButtonClicked}
                                loading={IsUpdateJobLoading}
                                buttonColor={theme.palette.common.white}
                                sx={{ width: '214px', height: 48 }}
                            >
                                {getMainButtonLabel()}
                            </PargodaRoundButton>
                        </Grid>
                    </Grid>
                </StyledSaveJobContainer>
                {process.env.VITE_ENV_TYPE === EnvTypes.LOCAL && (
                    <DevTool control={control} />
                )}
            </Form>
            <ProceedWithRecommendedModal
                isModalOpen={isProceedWithRecommendedModalOpen}
                onClickProceed={() => {
                    dispatch(
                        jobsActions.fetchJobRequirementsRecommendations({
                            jobId: Number(activeJob?.id),
                        })
                    );
                    dispatch(
                        jobsActions.setProceedWithRecommendedModalOpen(false)
                    );
                }}
            />
        </>
    );
};

export default SaveJob;
